/**
 * Durasan — wymiana bratniego produktu na karcie bez przeładowania strony.
 *
 * Rozmiar i kolor to osobne produkty (osobne URL), nie warianty Shopify.
 * Klik w .du-sib__opt pobiera stronę produktu, morphuje #MainContent
 * (nagłówek, stopka, koszyk zostają) i dopisuje historię. Pełny dokument
 * pozwala również podmienić metadane właściwego produktu.
 */
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { getViewParameterValue, startViewTransition } from '@theme/utilities';

const HISTORY_KEY = 'duSibling';
const STATEFUL_SELECTORS = ['media-gallery', 'sticky-add-to-cart', 'product-recommendations', 'du-product-discovery'];
const TITLE_SUFFIX = document.title.includes('|')
  ? ` ${document.title.slice(document.title.lastIndexOf('|'))}`
  : '';
const META_SELECTORS = [
  'meta[name="description"]',
  'meta[property="og:title"]',
  'meta[property="og:type"]',
  'meta[property="og:description"]',
  'meta[property="og:url"]',
  'meta[property="og:image"]',
  'meta[property="og:image:secure_url"]',
  'meta[property="og:image:width"]',
  'meta[property="og:image:height"]',
  'meta[property="og:price:amount"]',
  'meta[property="og:price:currency"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:image"]',
];

/** @type {Map<string, string>} */
const cache = new Map();

/** @type {AbortController | undefined} */
let inflight;
let displayedProductPath = window.location.pathname;

/**
 * @param {string | URL} href
 * @returns {string}
 */
function cacheKey(href) {
  const url = new URL(href, window.location.href);
  return `${url.origin}${url.pathname}`;
}

/**
 * @param {MouseEvent} event
 * @returns {boolean}
 */
function isModifiedClick(event) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

/**
 * @param {string} html
 * @returns {Document}
 */
function parseCached(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

/**
 * @param {string} href
 * @param {AbortSignal} [signal]
 * @returns {Promise<Document>}
 */
async function loadProductDocument(href, signal) {
  const key = cacheKey(href);
  const cached = cache.get(key);
  if (cached) return parseCached(cached);

  const target = new URL(href, window.location.href);
  target.search = '';
  const response = await fetch(target.toString(), {
    signal,
    headers: { 'X-Requested-With': 'fetch' },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const html = await response.text();
  const doc = parseCached(html);
  if (!doc.getElementById('MainContent') || !doc.querySelector('title')) {
    throw new Error('Incomplete product document');
  }
  cache.set(key, html);
  return doc;
}

/**
 * @param {Element} sourceSection
 * @returns {Record<string, Element>}
 */
function snapshotStateful(sourceSection) {
  /** @type {Record<string, Element>} */
  const snaps = {};
  for (const selector of STATEFUL_SELECTORS) {
    const el = sourceSection.querySelector(selector);
    if (el) snaps[selector] = /** @type {Element} */ (el.cloneNode(true));
  }
  return snaps;
}

/**
 * @param {Element} liveSection
 * @param {Record<string, Element>} snaps
 */
function restoreStateful(liveSection, snaps) {
  for (const [selector, clone] of Object.entries(snaps)) {
    const live = liveSection.querySelector(selector);
    if (live) {
      live.replaceWith(clone);
    }
  }
}

/**
 * @param {Document} htmlDoc
 * @param {URL} url
 */
function applyDocumentMeta(htmlDoc, url) {
  const serverTitle = htmlDoc.querySelector('title')?.textContent?.trim();
  const title = serverTitle ||
    htmlDoc.querySelector('.dx-product-headline__title')?.textContent?.trim() ||
    htmlDoc.querySelector('[data-testid="sticky-product-title"]')?.textContent?.trim();

  if (title) {
    document.title = serverTitle || (TITLE_SUFFIX && !title.includes('|') ? `${title}${TITLE_SUFFIX}` : title);
  }

  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', htmlDoc.querySelector('link[rel="canonical"]')?.getAttribute('href') || url.href);
  }

  // A sibling is a separate product page. Copy its complete server-rendered
  // metadata, and remove tags absent on the new page so an old image/price
  // cannot survive a client-side switch.
  for (const selector of META_SELECTORS) {
    const next = htmlDoc.querySelector(selector);
    const live = document.querySelector(selector);
    if (!next) {
      live?.remove();
    } else if (live) {
      live.setAttribute('content', next.getAttribute('content') || '');
    } else {
      document.head.append(next.cloneNode(true));
    }
  }

  const productId =
    htmlDoc.querySelector('product-form-component[data-product-id]')?.getAttribute('data-product-id') ||
    htmlDoc.querySelector('[data-product-id]')?.getAttribute('data-product-id');
  if (productId) RecentlyViewed.addProduct(productId);

  return { title, productId };
}

/**
 * @param {string} title
 */
function announce(title) {
  const picker = document.querySelector('[data-du-sibling-picker]');
  const live = document.querySelector('[data-du-sibling-live]');
  if (!live || !picker) return;
  const template = picker.getAttribute('data-du-sibling-announced') || '__TITLE__';
  live.textContent = template.replace('__TITLE__', title);
}

/**
 * @param {string} [value]
 */
function focusCurrentOption(value) {
  const picker = document.querySelector('[data-du-sibling-picker]');
  if (!(picker instanceof HTMLElement)) return;

  let current = value
    ? picker.querySelector(`[data-du-sib-value="${CSS.escape(value)}"]`)
    : null;
  if (!(current instanceof HTMLElement)) {
    current = picker.querySelector('.du-sib__opt--current');
  }
  if (!(current instanceof HTMLElement)) return;
  if (!current.hasAttribute('tabindex')) current.tabIndex = -1;
  current.focus({ preventScroll: true });
}

/**
 * @param {Document} htmlDoc
 * @param {URL} url
 */
function applyProductDom(htmlDoc, url) {
  const liveMain = document.getElementById('MainContent');
  const nextMain = htmlDoc.getElementById('MainContent');
  if (!liveMain || !nextMain) {
    throw new Error('Missing MainContent');
  }

  const nextById = new Map();
  for (const section of nextMain.querySelectorAll('.shopify-section')) {
    if (section.id) nextById.set(section.id, section);
  }

  let swapped = 0;
  for (const live of liveMain.querySelectorAll(':scope > .shopify-section')) {
    if (/reviews|zaufane/i.test(live.id)) continue;
    const next = nextById.get(live.id);
    if (!next) continue;
    const snaps = snapshotStateful(next);
    morph(live, next, MORPH_OPTIONS);
    restoreStateful(live, snaps);
    swapped += 1;
  }

  if (!swapped) {
    throw new Error('No product sections swapped');
  }

  const meta = applyDocumentMeta(htmlDoc, url);
  if (meta.title) announce(meta.title);

  document.dispatchEvent(
    new CustomEvent('durasan:sibling:changed', {
      bubbles: true,
      detail: { url: url.href, title: meta.title, productId: meta.productId },
    })
  );
}

/**
 * @param {boolean} pending
 */
function setPending(pending) {
  document.documentElement.classList.toggle('du-sib-pending', pending);
  document.querySelectorAll('[data-du-sibling-picker]').forEach((picker) => {
    picker.setAttribute('aria-busy', pending ? 'true' : 'false');
    const live = picker.querySelector('[data-du-sibling-live]');
    if (pending && live) {
      live.textContent = picker.getAttribute('data-du-sibling-loading') || '';
    }
  });
}

/**
 * @param {string} href
 * @param {{ push?: boolean, value?: string, force?: boolean }} [options]
 */
async function swapTo(href, { push = false, value, force = false } = {}) {
  if (window.Shopify?.designMode) {
    window.location.assign(href);
    return;
  }

  const url = new URL(href, window.location.href);
  url.hash = '';
  url.search = '';
  const view = getViewParameterValue();
  if (view) url.searchParams.set('view', view);

  const displayedUrl = document.querySelector('product-form-component')?.getAttribute('data-product-url');
  const displayedPath = displayedUrl ? new URL(displayedUrl, window.location.href).pathname : '';
  if (!force && displayedPath && displayedPath === url.pathname) {
    return;
  }

  inflight?.abort();
  inflight = new AbortController();
  const { signal } = inflight;

  setPending(true);
  document.documentElement.classList.add('du-sib-vt');

  try {
    const htmlDoc = await loadProductDocument(url, signal);
    if (signal.aborted) return;

    /** @type {unknown} */
    let applyError;
    await startViewTransition(() => {
      try {
        applyProductDom(htmlDoc, url);
      } catch (error) {
        applyError = error;
      }
    });
    if (applyError) throw applyError;
    displayedProductPath = url.pathname;

    if (push) {
      history.pushState({ [HISTORY_KEY]: true }, '', `${url.pathname}${url.search}`);
    }

    focusCurrentOption(value);
  } catch (error) {
    if (/** @type {Error} */ (error).name === 'AbortError') return;
    console.warn('[durasan-sibling-nav]', error);
    window.location.assign(url.href);
  } finally {
    document.documentElement.classList.remove('du-sib-vt');
    if (!signal.aborted) setPending(false);
  }
}

function seedHistory() {
  const state = history.state && typeof history.state === 'object' ? history.state : {};
  if (!state[HISTORY_KEY]) {
    history.replaceState({ ...state, [HISTORY_KEY]: true }, '', window.location.href);
  }
}

function onClick(event) {
  if (!(event.target instanceof Element)) return;
  const link = event.target.closest('a.du-sib__opt');
  if (!(link instanceof HTMLAnchorElement)) return;
  if (!link.closest('[data-du-sibling-picker]')) return;
  if (isModifiedClick(event) || link.target === '_blank') return;
  if (!document.getElementById('MainContent')) return;
  const destination = new URL(link.href, window.location.href);
  if (destination.hash && destination.pathname === window.location.pathname && destination.search === window.location.search) return;

  event.preventDefault();
  const value = link.getAttribute('data-du-sib-value') || undefined;
  swapTo(link.href, { push: true, value });
}

function onPopState() {
  // Native anchor navigation also emits popstate; it must retain normal scrolling.
  if (window.location.pathname === displayedProductPath) return;
  if (!document.querySelector('[data-du-sibling-picker]')) return;
  if (!/\/products\//.test(window.location.pathname)) {
    window.location.reload();
    return;
  }
  swapTo(window.location.href, { push: false, force: true });
}

function prefetchFrom(link) {
  if (!(link instanceof HTMLAnchorElement) || !link.href) return;
  loadProductDocument(link.href).catch(() => {});
}

document.addEventListener('click', onClick);
window.addEventListener('popstate', onPopState);

document.addEventListener(
  'pointerenter',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a.du-sib__opt');
    if (link) prefetchFrom(link);
  },
  true
);

if (document.querySelector('[data-du-sibling-picker]')) {
  seedHistory();
}
